import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './styles.css';
import TextureViewer3D from '../components/TextureViewer3D';
import { Card, Grid, Text, Tag as GeistTag, Button, useTheme } from '@geist-ui/core';
import { Home } from '@geist-ui/icons';

// Mock texture maps - these would normally come from an API or storage
const generateMockMaps = (textureName) => {
  const hash = textureName.split('').reduce((acc, char) => char.charCodeAt(0) + acc, 0);
  
  return {
    diffuse: `https://picsum.photos/seed/${hash + 1}/600/600`,
    normal: `https://picsum.photos/seed/${hash + 2}/600/600`,
    roughness: `https://picsum.photos/seed/${hash + 3}/600/600`,
    metallic: `https://picsum.photos/seed/${hash + 4}/600/600`,
    displacement: `https://picsum.photos/seed/${hash + 5}/600/600`,
    ambientOcclusion: `https://picsum.photos/seed/${hash + 6}/600/600`
  };
};

// Generate technical specifications based on texture name and material
const generateMockSpecs = (texture) => {
  const hash = texture.texture_name.split('').reduce((acc, char) => char.charCodeAt(0) + acc, 0);
  
  // Different specs based on material category
  let specs = {};
  
  switch(texture.category_name) {
    case 'Metals':
      specs = {
        hardness: Math.floor(hash % 10) + 1,
        conductivity: Math.floor((hash % 100) / 10) + 5,
        density: (Math.floor(hash % 200) / 10 + 7).toFixed(2),
        meltingPoint: Math.floor(hash % 1000) + 800,
        corrosionResistance: Math.floor(hash % 10) + 1
      };
      break;
    case 'Polymers':
      specs = {
        density: (Math.floor(hash % 20) / 10 + 0.8).toFixed(2),
        thermalStability: Math.floor(hash % 200) + 100,
        elasticity: Math.floor(hash % 100) + 1,
        impactResistance: Math.floor(hash % 10) + 1,
        uvaResistance: Math.floor(hash % 10) + 1
      };
      break;
    case 'Wood':
      specs = {
        hardness: Math.floor(hash % 10) + 1,
        density: (Math.floor(hash % 50) / 10 + 0.3).toFixed(2),
        moisture: Math.floor(hash % 15) + 5,
        grainPattern: ['Straight', 'Wavy', 'Spiral', 'Interlocked'][hash % 4],
        workability: Math.floor(hash % 10) + 1
      };
      break;
    default:
      specs = {
        density: (Math.floor(hash % 100) / 10 + 1).toFixed(2),
        hardness: Math.floor(hash % 10) + 1,
        texture: Math.floor(hash % 10) + 1,
        durability: Math.floor(hash % 10) + 1,
        thermalConductivity: (Math.floor(hash % 50) / 10).toFixed(2)
    };
  }
  
  return specs;
};

// Tag component with appropriate color coding
const Tag = ({ type, children }) => {
  const theme = useTheme();
  const isDark = theme.type === 'customDark';
  
  const getTagColor = () => {
    switch(type) {
      case 'tactile':
        return isDark ? { bg: '#3a3a3a', text: '#999' } : { bg: '#f5f5f5', text: '#666' };
      case 'aesthetic':
        return isDark ? { bg: '#3a3a3a', text: '#999' } : { bg: '#f5f5f5', text: '#666' };
      case 'technical':
        return isDark ? { bg: '#3a3a3a', text: '#999' } : { bg: '#f5f5f5', text: '#666' };
      default:
        return isDark ? { bg: '#3a3a3a', text: '#999' } : { bg: '#f5f5f5', text: '#666' };
    }
  };

  const colors = getTagColor();
  
  return (
    <GeistTag 
      type="lite"
      style={{
        borderRadius: '20px',
        backgroundColor: colors.bg,
        color: colors.text,
        margin: '2px'
      }}
    >
      {children}
    </GeistTag>
  );
};

// Map selector component
const MapSelector = ({ maps, activeMap, setActiveMap, availableMaps }) => {
  const mapLabels = {
    diffuse: 'Diffuse',
    normal: 'Normal',
    roughness: 'Roughness',
    metallic: 'Metallic',
    displacement: 'Height',
    ambientOcclusion: 'AO'
  };
  
  const theme = useTheme();
  const isDark = theme.type === 'customDark';

  // Funzione separata per gestire il click sui pulsanti
  const handleMapClick = (key) => {
    console.log(`Cliccato su ${key}, activeMap corrente: ${activeMap}`);
    
    if (availableMaps[key]) {
      if (activeMap === key) {
        // Deseleziona se è lo stesso
        console.log(`Deselezionando ${key}, impostando activeMap a null`);
        setActiveMap(null);
      } else {
        // Seleziona la nuova mappa
        console.log(`Selezionando ${key}, cambiando activeMap da ${activeMap} a ${key}`);
        setActiveMap(key);
      }
    }
  };

  return (
    <div className="map-selector-container">
      <div className="map-selector-grid">
        {Object.entries(mapLabels).map(([key, label]) => {
          const isActive = activeMap === key;
          const isAvailable = availableMaps[key] === true;
          
          return (
            <button
              key={key}
              className={`map-selector-button ${isActive ? 'active' : ''} ${!isAvailable ? 'disabled' : ''}`}
              onClick={() => handleMapClick(key)}
              disabled={!isAvailable}
              style={{
                backgroundColor: isActive 
                  ? (isDark ? '#666' : '#f0f0f0') 
                  : (isDark ? '#333' : '#ffffff'),
                color: !isAvailable
                  ? (isDark ? '#555' : '#cccccc')
                  : isDark 
                    ? (isActive ? '#ffffff' : '#aaaaaa') 
                    : (isActive ? '#444444' : '#888888'),
                borderColor: isActive
                  ? (isDark ? '#888' : '#cccccc')
                  : (isDark ? '#555' : '#e0e0e0'),
                transition: 'all 0.2s ease',
                boxShadow: 'none',
                opacity: !isAvailable ? 0.5 : 1,
                cursor: !isAvailable ? 'not-allowed' : 'pointer'
              }}
            >
              <div className="map-button-content">
                <span className="map-button-label">{label}</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Bento Grid Card component
const BentoCard = ({ title, children, className = "" }) => {
  const theme = useTheme();
  const isDark = theme.type === 'customDark';
  
  return (
    <Card width="100%" shadow style={{ 
      borderRadius: '20px', 
      overflow: 'hidden',
      margin: '10px 0',
      background: isDark ? '#333' : undefined,
      border: isDark ? '1px solid #444' : undefined,
      boxShadow: isDark ? '0 4px 12px rgba(0, 0, 0, 0.15)' : '0 4px 12px rgba(0, 0, 0, 0.07)'
    }} className={className}>
      {title && (
        <Card.Content style={{ padding: '10px 15px' }}>
          <Text h4 style={{ 
            margin: 0, 
            textTransform: 'uppercase', 
            fontSize: '0.9rem', 
            letterSpacing: '0.05em',
            color: isDark ? '#aaa' : '#666'
          }}>{title}</Text>
        </Card.Content>
      )}
      <Card.Content style={{ 
        padding: '15px', 
        background: 'transparent',
        color: isDark ? '#eaeaea' : undefined
      }}>
        {children}
      </Card.Content>
    </Card>
  );
};

function Page2() {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeMap, setActiveMap] = useState(null);
  const [texture, setTexture] = useState(null);
  const [maps, setMaps] = useState({});
  const [specs, setSpecs] = useState({});
  const theme = useTheme();
  const isDark = theme.type === 'customDark';
  
  // Aggiungo stato per le mappe disponibili
  const [availableMaps, setAvailableMaps] = useState({
    diffuse: false,
    normal: false,
    roughness: false,
    displacement: false,
    ambientOcclusion: false,
    metallic: false
  });
  
  // Handler per aggiornare le mappe disponibili
  const handleMapAvailabilityChange = (mapAvailability) => {
    console.log("Disponibilità mappe aggiornata:", mapAvailability);
    setAvailableMaps(mapAvailability);
    
    // Se active è specificato in mapAvailability, lo utilizziamo
    if (mapAvailability.active !== undefined) {
      console.log("Impostando active da mapAvailability:", mapAvailability.active);
      setActiveMap(mapAvailability.active);
    }
  };
  
  // Effetto per modificare gli stili a livello di documento
  useEffect(() => {
    // Reset all potential spacing
    document.documentElement.style.height = '100%';
    document.documentElement.style.margin = '0';
    document.documentElement.style.padding = '0';
    document.documentElement.style.overflow = 'hidden';
    
    document.body.style.height = '100%';
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.overflow = 'hidden';
    
    const rootElement = document.getElementById('root');
    if (rootElement) {
      rootElement.style.height = '100%';
      rootElement.style.margin = '0';
      rootElement.style.padding = '0';
    }
    
    // Clean up function
    return () => {
      document.documentElement.style.height = '';
      document.documentElement.style.margin = '';
      document.documentElement.style.padding = '';
      document.documentElement.style.overflow = '';
      
      document.body.style.height = '';
      document.body.style.margin = '';
      document.body.style.padding = '';
      document.body.style.overflow = '';
      
      if (rootElement) {
        rootElement.style.height = '';
        rootElement.style.margin = '';
        rootElement.style.padding = '';
      }
    };
  }, []);
  
  useEffect(() => {
    // Get texture data from location state or redirect back to page1
    if (location.state && location.state.texture) {
      setTexture(location.state.texture);
      setMaps(generateMockMaps(location.state.texture.texture_name));
      setSpecs(generateMockSpecs(location.state.texture));
    } else {
      navigate('/');
    }
  }, [location, navigate]);
  
  if (!texture) {
    return <div className="loading">Loading texture details...</div>;
  }
  
  // Generate color for the texture
  const getTextureColor = () => {
    const categoryBaseColors = {
      "Metals": 210,
      "Polymers": 120,
      "Ceramics": 30,
      "Wood": 40,
      "Fabrics": 300,
      "Composites": 180,
      "Stone": 0,
      "Organics": 20,
      "Misc.": 270
    };
    
    const hashString = (str) => {
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
      }
      return Math.abs(hash);
    };
    
    const hue = categoryBaseColors[texture.category_name] || 0;
    const textureHash = hashString(texture.texture_name || "");
    const saturation = 50 + (textureHash % 30);
    const lightness = 40 + (textureHash % 30);
    
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };
  
  // Process tags
  const processTags = (tagString) => {
    if (!tagString) return [];
    return typeof tagString === 'string' ? tagString.split(' ') : 
           Array.isArray(tagString) ? tagString : [];
  };
  
  const tactileTags = processTags(texture.tactile_tags);
  const aestheticTags = processTags(texture.aesthetic_tags);
  const technicalTags = processTags(texture.technical_tags);
  
  return (
    <div className="bento-page" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: '100vw',
      height: '100vh',
      margin: 0,
      padding: 0,
      backgroundColor: isDark ? '#222' : '#ffffff',
      overflow: 'hidden'
    }}>
      {/* Home button in top-left corner */}
      <Link to="/" style={{
        position: 'fixed',
        top: '20px',
        left: '20px',
        width: '40px',
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        background: isDark ? '#444' : '#eaeaea',
        borderRadius: '50%',
        transition: 'all 0.3s ease',
        zIndex: 2000,
        boxShadow: isDark 
          ? '0 0 15px rgba(100, 100, 255, 0.15), 0 0 5px rgba(150, 150, 255, 0.1)' 
          : '0 5px 10px rgba(0, 0, 0, 0.1)'
      }}>
        <Home size={22} color={isDark ? '#fff' : '#000'} />
      </Link>
      
      <div className="split-layout" style={{
        display: 'flex',
        width: '100%',
        height: '100%',
        margin: 0,
        padding: 0
      }}>
        {/* Left half: 3D viewer */}
        <div className="left-column" style={{
          flex: '1 0 50%',
          height: '100%',
          backgroundColor: isDark ? '#1a1a1a' : '#fafafa'
        }}>
          <TextureViewer3D 
            maps={maps} 
            activeMap={activeMap} 
            textureColor={getTextureColor()}
            disableControlButton={true}
            texture={texture}
            onMapAvailabilityChange={handleMapAvailabilityChange}
          />
        </div>
        
        {/* Right half: Title and bento cards */}
        <div className="right-column" style={{
          flex: '1 0 50%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: isDark ? '#222' : '#ffffff',
          overflow: 'hidden'
        }}>
          {/* Large Title */}
          <div className="texture-title" style={{
            height: '20vh',
            padding: '0 20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderBottom: `1px solid ${isDark ? '#333' : '#eaeaea'}`,
            backgroundColor: isDark ? '#222' : '#ffffff'
          }}>
            <Text h1 style={{ color: isDark ? '#bbb' : '#666' }}>{texture.texture_name}</Text>
          </div>
          
          {/* Bento Grid */}
          <div className="bento-grid" style={{
            flex: '1 1 auto',
            height: 'calc(100% - 20vh)',
            padding: '16px 24px',
            overflowY: 'auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gridTemplateRows: 'auto 1fr auto', /* Prima riga auto, seconda 1fr (espandibile), terza auto */
            gap: '8px', /* Ridotto lo spazio tra le bento card */
            backgroundColor: isDark ? '#222' : '#ffffff'
          }}>
            {/* Material Info */}
            <BentoCard className="auto-height" title="Material Information">
              <div style={{ padding: '0 8px', background: 'transparent' }}>
                <Grid.Container gap={1}>
                  <Grid xs={12} md={6}>
                    <div className="info-item" style={{ 
                      color: isDark ? '#aaa' : '#777',
                      paddingTop: '4px',
                      paddingBottom: '4px',
                      background: 'transparent'
                    }}>
                      <Text small b style={{ color: isDark ? '#888' : '#777', marginBottom: '4px' }}>Material</Text>
                      <Text style={{ color: isDark ? '#ccc' : '#555', margin: 0 }}>{texture.material_name}</Text>
                    </div>
                  </Grid>
                  <Grid xs={12} md={6}>
                    <div className="info-item" style={{ 
                      color: isDark ? '#aaa' : '#777',
                      paddingTop: '4px',
                      paddingBottom: '4px',
                      background: 'transparent'
                    }}>
                      <Text small b style={{ color: isDark ? '#888' : '#777', marginBottom: '4px' }}>Category</Text>
                      <Text style={{ color: isDark ? '#ccc' : '#555', margin: 0 }}>{texture.category_name}</Text>
                    </div>
                  </Grid>
                  {texture.description && (
                    <Grid xs={24}>
                      <div className="info-item" style={{ 
                        color: isDark ? '#aaa' : '#777',
                        paddingTop: '4px',
                        paddingBottom: '4px',
                        background: 'transparent'
                      }}>
                        <Text small b style={{ color: isDark ? '#888' : '#777', marginBottom: '4px' }}>Description</Text>
                        <Text style={{ color: isDark ? '#ccc' : '#555', margin: 0 }}>{texture.description}</Text>
                      </div>
                    </Grid>
                  )}
                </Grid.Container>
              </div>
            </BentoCard>
            
            {/* Map Controls */}
            <BentoCard className="auto-height" title="Texture Maps">
              <div style={{ padding: '0 8px', background: 'transparent' }}>
                <MapSelector 
                  maps={maps}
                  activeMap={activeMap}
                  setActiveMap={setActiveMap}
                  availableMaps={availableMaps}
                />
              </div>
            </BentoCard>
            
            {/* Technical Specs - occupa più spazio */}
            <BentoCard className="auto-height full-width tech-specs-card" title="Technical Specifications" style={{
              gridRow: '2 / 3', /* Posizionato nella seconda riga che è espandibile (1fr) */
              display: 'flex',
              flexDirection: 'column',
              height: '100%', /* Riempie tutto lo spazio disponibile */
              boxShadow: isDark ? 'none' : '0 4px 12px rgba(0, 0, 0, 0.15)'
            }}>
              <div 
                className={`tech-specs-list-container ${isDark ? 'dark-theme' : ''}`}
                style={{
                  '--item-count': Object.keys(specs).length,
                  gap: '0px',
                  marginBottom: '0',
                  marginTop: '0',
                  padding: '6px 10px 6px 6px',
                  flex: '1 1 auto', /* Fa espandere il contenitore */
                  height: '100%'
                }}
              >
                {Object.entries(specs).map(([key, value], index) => {
                  // Calcola un valore di percentuale per le proprietà numeriche
                  const numericValue = !isNaN(parseFloat(value)) ? parseFloat(value) : null;
                  const isNumeric = numericValue !== null;
                  
                  // Determina il range massimo per diversi tipi di specifiche
                  let maxValue = 10; // Default max
                  if (key.includes('density')) maxValue = 20;
                  if (key.includes('Temperature') || key.includes('melting') || key.includes('thermal')) maxValue = 1000;
                  if (key.includes('conductivity')) maxValue = 20;
                  
                  // Calcola la percentuale per la barra di progresso
                  const percentage = isNumeric ? Math.min(100, (numericValue / maxValue) * 100) : 0;
                  
                  // Determina l'unità di misura appropriata
                  let unit = '';
                  if (key.includes('density')) unit = 'g/cm³';
                  if (key.includes('Temperature') || key.includes('melting')) unit = '°C';
                  if (key.includes('conductivity') && !key.includes('thermal')) unit = 'S/m';
                  if (key.includes('thermal')) unit = 'W/mK';
                  
                  return (
                    <div 
                      className="tech-spec-list-item" 
                      key={key}
                      style={{
                        // Calcolo per riempire meglio lo spazio
                        height: `calc(${100 / Object.keys(specs).length}% - ${Object.keys(specs).length > 4 ? '10px' : '6px'})`,
                        maxHeight: '90px' // Aumentata altezza massima
                      }}
                    >
                      <div className="tech-spec-label">
                          {key.charAt(0).toUpperCase() + key.slice(1)}
                      </div>
                      
                      <div className="tech-spec-value-container">
                        <div className="tech-spec-value">
                          <strong style={{ color: isDark ? '#ddd' : '#555' }}>{value}</strong>
                          {unit && <span className="tech-spec-unit" style={{ color: isDark ? '#999' : '#777' }}>{unit}</span>}
                        </div>
                        
                        {isNumeric && (
                          <div className="tech-spec-slider">
                            <div className="tech-spec-slider-track" style={{ backgroundColor: isDark ? '#444' : '#e0e0e0' }}>
                              <div 
                                className="tech-spec-slider-fill" 
                                style={{ 
                                  width: `${percentage}%`,
                                  backgroundColor: isDark ? '#777' : '#999'
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </BentoCard>
            
            {/* All Tags Combined - dimensione proporzionale al contenuto */}
            <BentoCard className="auto-height full-width tags-card" title="Texture Tags" style={{
              gridRow: '3 / 4', /* Posizionato nella terza riga che è auto (si adatta al contenuto) */
              height: 'min-content',
              marginBottom: '0',
              minHeight: 'unset'
            }}>
              <div className="tags-unified-container" style={{ 
                padding: '6px 8px', /* Padding uniforme */
                background: 'transparent',
                display: 'flex',
                flexDirection: 'column',
                height: 'min-content'
              }}>
                {/* Rimosso il container delle etichette delle categorie */}
                
                {/* Cloud dei tag unificato - singola riga */}
                <div className="tags-cloud-unified" style={{ 
                  display: 'flex', 
                  flexWrap: 'nowrap', 
                  gap: '6px',  
                  background: 'transparent',
                  width: '100%',
                  paddingBottom: '0',
                  overflowX: 'auto',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  WebkitOverflowScrolling: 'touch'
                }}>
                  {/* Tutti i tag in un'unica riga */}
                  {[...tactileTags, ...aestheticTags, ...technicalTags].map((tag, index) => {
                    // Determina il tipo di tag
                    let tagType = '';
                    let tagStyle = {};
                    
                    if (tactileTags.includes(tag)) {
                      tagType = 'tactile';
                      tagStyle = {
                        background: isDark ? 'rgba(30, 100, 190, 0.1)' : 'rgba(230, 242, 255, 0.6)',
                        color: isDark ? '#a8c7ff' : '#2d5ca8',
                        border: `1px solid ${isDark ? 'rgba(126, 182, 255, 0.2)' : 'rgba(58, 123, 219, 0.15)'}`
                      };
                    } else if (aestheticTags.includes(tag)) {
                      tagType = 'aesthetic';
                      tagStyle = {
                        background: isDark ? 'rgba(190, 70, 130, 0.1)' : 'rgba(255, 235, 245, 0.6)',
                        color: isDark ? '#ffb3d1' : '#a8366c',
                        border: `1px solid ${isDark ? 'rgba(255, 156, 194, 0.2)' : 'rgba(219, 76, 140, 0.15)'}`
                      };
                    } else if (technicalTags.includes(tag)) {
                      tagType = 'technical';
                      tagStyle = {
                        background: isDark ? 'rgba(70, 180, 70, 0.1)' : 'rgba(230, 255, 230, 0.6)',
                        color: isDark ? '#a8e8a4' : '#2d7f29',
                        border: `1px solid ${isDark ? 'rgba(146, 232, 138, 0.2)' : 'rgba(76, 170, 68, 0.15)'}`
                      };
                    }
                    
                    return (
                      <div 
                        key={`tag-${index}`} 
                        className={`tag-chip ${tagType}`}
                        style={{
                          padding: '7px 14px',
                          borderRadius: '16px',
                          fontSize: '15px',
                          boxShadow: 'none',
                          transition: 'all 0.15s ease',
                          lineHeight: '1.2',
                          marginBottom: '0',
                          flexShrink: 0,
                          whiteSpace: 'nowrap',
                          ...tagStyle
                        }}
                      >
                        {tag}
                      </div>
                    );
                  })}
                      </div>
                    </div>
            </BentoCard>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Page2;